import { useEffect, useRef } from "react";

/**
 * useAutoLogout
 * Triggers the provided logout function after a period of user inactivity (default: 5 minutes).
 * Listens for mouse, keyboard, touch, and scroll events to reset the timer.
 * @param logoutFn - Function to call on auto-logout
 * @param delayMs - Inactivity delay in milliseconds (default: 5 * 60 * 1000)
 */
export function useAutoLogout(logoutFn: () => void, delayMs: number = 15 * 60 * 1000) {
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!logoutFn) return;

    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        logoutFn();
      }, delayMs);
    };

    const events = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
    ];
    events.forEach(event => window.addEventListener(event, resetTimer));
    resetTimer();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach(event => window.removeEventListener(event, resetTimer));
    };
  }, [logoutFn, delayMs]);
}
